'use memo';

import { useEffect, useState } from 'react';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography';
import { cn } from '@/lib/cn';
import { defaultNS, useTranslation } from '@/lib/i18n';
import type { AlertsTrendComparisonResponse } from '@/routes/_staff/accesses/_layout/alerts/-query/type';
import { BentoCard } from './BentoCard';
const CHART_HEIGHT = 120;

/** changePercentage から前月の値を逆算する（推移 API 未提供のための暫定）。 */
function derivePrev(current: number, changePercentage: number | null): number {
  if (changePercentage === null) return current;
  const denom = 1 + changePercentage / 100;
  return denom > 0 ? Math.round(current / denom) : 0;
}
type StackColumn = {
  label: string;
  it: number;
  id: number;
  ai: number;
};
function StackedColumn({
  column,
  maxTotal,
  grown
}: {
  column: StackColumn;
  maxTotal: number;
  /** マウント後に true。0→実値へ伸びるアニメーション用 */
  grown: boolean;
}) {
  const total = column.it + column.id + column.ai;
  const px = (v: number) => v / maxTotal * CHART_HEIGHT;
  return <div className="flex w-14 flex-col items-center gap-1.5">
      {/* 合計値ラベル */}
      <Text variant="labelSmall" className="text-neutral-on-fill">
        {total.toLocaleString()}
      </Text>
      {/* 棒（マウント時に下から伸びる） */}
      <div className="flex w-full flex-col-reverse overflow-hidden rounded-t transition-[height] duration-700 ease-out" style={{
      height: grown ? `${px(total)}px` : '0px'
    }}>
        <span className="block bg-slate-600" style={{
        height: `${px(column.it)}px`
      }} />
        <span className="block bg-slate-400" style={{
        height: `${px(column.id)}px`
      }} />
        <span className="block bg-slate-300" style={{
        height: `${px(column.ai)}px`
      }} />
      </div>
    </div>;
}
function LegendItem({
  colorClass,
  label
}: {
  colorClass: string;
  label: string;
}) {
  return <HStack className="items-center gap-1.5">
      <span className={cn('size-2.5 rounded-sm', colorClass)} />
      <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
        {label}
      </Text>
    </HStack>;
}

/** カード5: シャドー検知の推移（前月→今月・種別別）。推移 API 未提供のため2点表示。 */
export function ShadowTrendCard({
  data,
  className
}: {
  data: AlertsTrendComparisonResponse | undefined;
  className?: string;
}) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.accesses.overview.bento'
  });
  const {
    t: tAlerts
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.accesses.alerts.sideTabs'
  });

  // マウント直後に棒を 0 から実値へ伸ばす（メンバー概要風の出現）
  const [grown, setGrown] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setGrown(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const itNow = data?.shadowIt.currentMonthCount ?? 0;
  const idNow = data?.shadowId.currentMonthCount ?? 0;
  const aiNow = data?.shadowAi.currentMonthCount ?? 0;
  const prev: StackColumn = {
    label: t('shadowTrend.prevMonthLabel'),
    it: derivePrev(itNow, data?.shadowIt.changePercentage ?? null),
    id: derivePrev(idNow, data?.shadowId.changePercentage ?? null),
    ai: derivePrev(aiNow, data?.shadowAi.changePercentage ?? null)
  };
  const now: StackColumn = {
    label: t('shadowTrend.thisMonthLabel'),
    it: itNow,
    id: idNow,
    ai: aiNow
  };
  const maxTotal = Math.max(1, prev.it + prev.id + prev.ai, now.it + now.id + now.ai);
  return <BentoCard className={className} title={t('shadowTrend.title')}>
      <VStack className="mt-2 flex-1 justify-center gap-3">
        <HStack className="items-stretch gap-3">
          {/* Y軸（0 と 最大値の目盛り） */}
          <VStack className="shrink-0 justify-between pb-5 text-right" style={{
          height: `${CHART_HEIGHT + 40}px`
        }}>
            <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
              {maxTotal.toLocaleString()}
            </Text>
            <Text variant="labelSmallReg" className="text-neutral-muted-on-fill">
              0
            </Text>
          </VStack>

          {/* プロット領域：ベースラインのみ */}
          <div className="relative flex-1">
            <div className="relative flex items-end justify-evenly border-b border-neutral-muted-border" style={{
            height: `${CHART_HEIGHT + 22}px`
          }}>
              <StackedColumn column={prev} maxTotal={maxTotal} grown={grown} />
              <StackedColumn column={now} maxTotal={maxTotal} grown={grown} />
            </div>
            {/* 月ラベル（列の真下に揃える） */}
            <div className="flex justify-evenly pt-1.5">
              <Text variant="labelSmallReg" className="w-14 text-center text-neutral-muted-on-fill">
                {prev.label}
              </Text>
              <Text variant="labelSmallReg" className="w-14 text-center text-neutral-muted-on-fill">
                {now.label}
              </Text>
            </div>
          </div>
        </HStack>

        <HStack className="justify-center gap-4">
          <LegendItem colorClass="bg-slate-600" label={tAlerts('shadowIT')} />
          <LegendItem colorClass="bg-slate-400" label={tAlerts('shadowID')} />
          <LegendItem colorClass="bg-slate-300" label={tAlerts('shadowAI')} />
        </HStack>
      </VStack>
    </BentoCard>;
}